import { useEffect, useState } from "react";

/**
 * Reactive nullable-boolean indicating whether the user prefers reduced motion.
 * Returns `null` until the first client-side resolution (matches Motion's
 * `useReducedMotion` semantics) so SSR-rendered content avoids the
 * "animate-then-snap" flash that a plain `false` default would cause.
 *
 *   const reduced = useReducedMotion();
 *   <motion.div animate={reduced === false ? { opacity: 1, y: 0 } : {}} />
 *
 *   // Or treat null as "unknown, default to allowing motion":
 *   const animateAllowed = reduced === false || reduced === null;
 *
 * Works regardless of motion library — does not import from `motion/react` or
 * `framer-motion`. Same shape as Motion's hook so consumers can swap freely.
 */
export function useReducedMotion(): boolean | null {
  const [reduced, setReduced] = useState<boolean | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);

    if ("addEventListener" in mq) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    (mq as MediaQueryList).addListener(handler);
    return () => (mq as MediaQueryList).removeListener(handler);
  }, []);

  return reduced;
}
